import { bunja } from 'bunja';
import { atom } from 'jotai';

import { OrderTypeEnum } from '@flipster/apis/types';
import { i18nBunja } from '@flipster/i18n';
import { accountBunja } from '@flipster/r230522/original/account';
import { ConditionalOrderTypeEnum, ordersBunja } from '@flipster/r230522/original/order';
import { Col } from '@flipster/ui/Table';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { yourPositionsBunja } from '../state';

export const pendingPositionTableBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const { symbol } = bunja.use(tradeSymbolBunja);
  const { v2AccountAtom } = bunja.use(accountBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { showAllSymbolAtom } = bunja.use(yourPositionsBunja);
  const t = getT();

  const pendingPositionTypeAtom = atom<'limit' | 'trigger'>('limit');

  const isOneWayModeAtom = atom((get) => {
    const account = get(v2AccountAtom);
    return account?.tradeMode === 'OneWay';
  });

  const pendingOrdersAtom = atom((get) => {
    const allPendingOrders = get(pendingPositionsAtom);
    const showAllSymbol = get(showAllSymbolAtom);

    return showAllSymbol ? Object.values(allPendingOrders ?? {}).flat() : (allPendingOrders?.[symbol] ?? []);
  });

  /**
   * Limit 탭 분류:
   * - 일반 Limit 주문 (`ORDER_TYPE_LIMIT`)
   * - Triggered-but-unfilled Limit TP/SL — 포지션 TP/SL이 발동되어 호가창에 얹힌 limit.
   *   `orderType=STOP_LIMIT` + `conditionalOrderType=PositionTp/PositionSl`
   * - Triggered standalone Trigger Limit — trigger price를 찍고 limit book에 안착한 행.
   *   `orderType=STOP_LIMIT` + `conditionalOrderType=Invalid` + `waitingTrigger=false`
   *
   * Trigger price 대기 중인 standalone Trigger Limit (waitingTrigger !== false)은 Trigger 탭에 그대로 둔다 (ERD-216).
   */
  const limitOrdersAtom = atom((get) => {
    const pendingOrders = get(pendingOrdersAtom);
    return pendingOrders.filter((item) => {
      if (item.orderType === OrderTypeEnum['Limit']) return true;
      if (item.orderType === OrderTypeEnum['StopLimit']) {
        if (
          item.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ||
          item.conditionalOrderType === ConditionalOrderTypeEnum.PositionSl
        ) {
          return true;
        }
        return item.conditionalOrderType === ConditionalOrderTypeEnum.Invalid && item.waitingTrigger === false;
      }
      return false;
    });
  });

  /**
   * Trigger 탭 분류:
   * - StopMarket — Trigger Market 주문
   * - StopLimit + `conditionalOrderType=Invalid` + 아직 trigger 대기 중 (waitingTrigger !== false) — Trigger Limit standalone (ERD-216)
   */
  const triggerOrdersAtom = atom((get) => {
    const pendingOrders = get(pendingOrdersAtom);
    return pendingOrders.filter((item) => {
      if (item.orderType === OrderTypeEnum['StopMarket']) return true;
      if (
        item.orderType === OrderTypeEnum['StopLimit'] &&
        item.conditionalOrderType === ConditionalOrderTypeEnum.Invalid &&
        item.waitingTrigger !== false
      ) {
        return true;
      }
      // 위 분류에 속하지 않는 비 Limit 주문 (e.g. 그 외 trigger 변형)도 Trigger 탭에 보존
      return item.orderType !== OrderTypeEnum['Limit'] && item.orderType !== OrderTypeEnum['StopLimit'];
    });
  });

  const hasAnyTriggerTimeAtom = atom((get) => {
    const triggerOrders = get(triggerOrdersAtom);
    return triggerOrders.some((order) => order.triggerTimestamp);
  });

  const pendingOrderCountAtom = atom((get) => {
    const limitOrders = get(limitOrdersAtom);
    const triggerOrders = get(triggerOrdersAtom);

    return {
      limit: limitOrders.length,
      trigger: triggerOrders.length,
    };
  });

  const selectedPendingOrdersAtom = atom((get) => {
    const selectedOrderType = get(pendingPositionTypeAtom);
    const limitOrders = get(limitOrdersAtom);
    const triggerOrders = get(triggerOrdersAtom);

    switch (selectedOrderType) {
      case 'limit':
        return limitOrders;
      case 'trigger':
        return triggerOrders;
      default:
        return [];
    }
  });

  const tpslCols: Col[] = [
    { key: 'take-profit', title: t('Trade.r250401.Take_profit_label'), width: 106, minWidth: 106 },
    { key: 'stop-loss', title: t('Trade.r250401.Stop_loss_label'), width: 106, minWidth: 106 },
  ];
  const reduceOnlyCol: Col = { key: 'reduce-only', title: t('Assets.r250401.Reduce_only'), width: 90, minWidth: 90 };
  const cancelCol: Col = { key: 'cancel', title: t('Assets.r250401.Cancel_order'), align: 'right', minWidth: 78 };

  const limitColumnsAtom = atom<Col[]>((get) => {
    const isOneWayMode = get(isOneWayModeAtom);

    const baseCols: Col[] = [
      { key: 'symbol', title: t('Assets.r250401.Symbol'), width: 90, minWidth: 90 },
      { key: 'side-leverage', title: t('Trade.r250401.Side_leverage_label'), width: 75, minWidth: 75 },
      { key: 'type', title: t('Autogen_common.r251126.s_Type'), width: 110, minWidth: 110 },
      { key: 'price', title: t('Trade.r251104.Limit_price'), width: 90, minWidth: 90 },
      { key: 'qty-value', title: t('Trade.r250827.Qty_or_value'), width: 130, minWidth: 130 },
      { key: 'filled', title: t('Trade.r260414.Filled'), width: 130, minWidth: 130 },
    ];

    return [...baseCols, ...tpslCols, ...(isOneWayMode ? [reduceOnlyCol] : []), cancelCol];
  });

  const triggerColumnsAtom = atom<Col[]>((get) => {
    const isOneWayMode = get(isOneWayModeAtom);
    const hasAnyTriggerTime = get(hasAnyTriggerTimeAtom);

    const baseCols: Col[] = [
      { key: 'symbol', title: t('Assets.r250401.Symbol'), width: 90, minWidth: 90 },
      { key: 'side-leverage', title: t('Trade.r250401.Side_leverage_label'), width: 75, minWidth: 75 },
      { key: 'price', title: t('Trade.r250401.Trigger_price'), width: 90, minWidth: 90 },
      // Trigger Limit 주문 (Limit price / BBO) 또는 Trigger Market을 표시 (ERD-216)
      { key: 'order-price', title: t('Trade.r260420.Order_price'), width: 100, minWidth: 100 },
      { key: 'qty-value', title: t('Trade.r250827.Qty_or_value'), width: 130, minWidth: 130 },
      ...(hasAnyTriggerTime
        ? [{ key: 'trigger-time', title: t('Trade.r250401.Trigger_time'), width: 90, minWidth: 90 } satisfies Col]
        : []), // triggerTimestamp가 하나라도 있는 경우 노출
    ];

    return [...baseCols, ...tpslCols, ...(isOneWayMode ? [reduceOnlyCol] : []), cancelCol];
  });

  const pendingTableColumnsAtom = atom<Col[]>((get) => {
    const selectedOrderType = get(pendingPositionTypeAtom);
    const limitColumns = get(limitColumnsAtom);
    const triggerColumns = get(triggerColumnsAtom);

    switch (selectedOrderType) {
      case 'limit':
        return limitColumns;
      case 'trigger':
        return triggerColumns;
      default:
        return [];
    }
  });

  return {
    pendingPositionTypeAtom,
    pendingTableColumnsAtom,
    pendingOrdersAtom,
    pendingOrderCountAtom,
    hasAnyTriggerTimeAtom,
    selectedPendingOrdersAtom,
    showAllSymbolAtom,
  };
});
pendingPositionTableBunja.debugLabel = 'pendingPositionTableBunja';
